import { SectionKey } from '../types';

export interface RejectionCheck {
  field: string;
  section: SectionKey;
  detailsField: string;
  label: string;
}

/** All workflow rejection field mappings across supported payment workflows. */
export const REJECTION_CHECKS: RejectionCheck[] = [
  // Dubai Payment Approval
  {
    field: 'resp_approve',
    section: 'resp_lawyer',
    detailsField: 'resp_reject_details',
    label: 'Responsible Lawyer rejected the payment',
  },
  {
    field: 'first_approver',
    section: 'first_partner',
    detailsField: 'first_reject_details',
    label: 'First Approver rejected the payment',
  },
  {
    field: 'second_approver',
    section: 'second_partner',
    detailsField: 'second_reject_details',
    label: 'Second Approver rejected the payment',
  },
  {
    field: 'payment_confirmation',
    section: 'payment_confirmation',
    detailsField: 'payment_rejection_details',
    label: 'Payment Confirmation rejected the payment',
  },
  // Dubai Payment From Office Account
  {
    field: 'approverAuthApprove',
    section: 'dubai_payment_from_office_account_approver',
    detailsField: 'approverRejectionDetails',
    label: 'Approver rejected the payment',
  },
  {
    field: 'paymentFromOfficePaymentConfirmation',
    section: 'dubai_payment_from_office_account_payment_confirmation',
    detailsField: 'paymentFromOfficePaymentRejectionDetails',
    label: 'Payment Confirmation rejected the payment',
  },
  // Dubai Transfer Requisition
  {
    field: 'firstPartnerApproval',
    section: 'dubai_transfer_requisition_first_partner',
    detailsField: 'firstPartnerRejectionDetails',
    label: 'First Partner rejected the payment',
  },
  {
    field: 'secondPartnerApproval',
    section: 'dubai_transfer_requisition_second_partner',
    detailsField: 'secondPartnerRejectionDetails',
    label: 'Second Partner rejected the payment',
  },
  {
    field: 'transfer_requisition_payment_confirmation',
    section: 'dubai_transfer_requisition_payment_confirmation',
    detailsField: 'transfer_requisition_payment_rejection_details',
    label: 'Payment Confirmation rejected the payment',
  },
  // Overtime Approval
  {
    field: 'confirm_approval_overtime_request',
    section: 'overtime_approval_line_manager',
    detailsField: 'line_manager_comments',
    label: 'Line Manager rejected the overtime request',
  },
  {
    field: 'confirmation',
    section: 'overtime_approval_finance_confirmation',
    detailsField: 'confirmation',
    label: 'Finance rejected the overtime request',
  },
];

export interface RejectionReason {
  label: string;
  details: string;
}

const isRejectedValue = (value: unknown): boolean => value === 'no';

/**
 * Returns true when any rejection approval field is set to 'no'.
 * Used at submit time before section completion state is updated.
 */
export const isFormDataRejected = (
  formData: Record<string, unknown>
): boolean => {
  if (formData?.currentAuthStage === 'Rejected') {
    return true;
  }

  return REJECTION_CHECKS.some(({ field }) =>
    isRejectedValue(formData?.[field])
  );
};

/**
 * Returns true when a rejection field is 'no' and its section is completed or submitted,
 * or when the workflow auth stage is Rejected.
 */
export const isFormRejected = (
  formData: Record<string, unknown>,
  completedSections: SectionKey[],
  submittedSections: SectionKey[] = []
): boolean => {
  if (formData?.currentAuthStage === 'Rejected') {
    return true;
  }

  return REJECTION_CHECKS.some(({ field, section }) => {
    if (!isRejectedValue(formData?.[field])) {
      return false;
    }

    return (
      completedSections.includes(section) ||
      submittedSections.includes(section)
    );
  });
};

/**
 * Returns the first matching rejection reason for display in the rejection banner.
 */
export const getRejectionReason = (
  formData: Record<string, unknown>
): RejectionReason | null => {
  for (const { field, detailsField, label } of REJECTION_CHECKS) {
    if (isRejectedValue(formData?.[field])) {
      const details = formData?.[detailsField];
      if (details) {
        return {
          label,
          details: String(details),
        };
      }
    }
  }

  return null;
};
